/**
 * Recognition celebrations in Gyro Admin (gyro-svc desktopMascotCelebrations.ts, mascotCelebrationPack.ts;
 * docs/PLAN-recognition-celebration-2026-10-21.md section 4.5; owner, 2026-10-21), without React: the celebration packs
 * the import tool registered (GET /v1/admin/desktop/mascot/celebrations) with their switches, what Save sends
 * (PUT /v1/admin/desktop/mascot/celebrations: every pack's switch with the revision the section loaded), and the
 * section's wording. The console is advisory: the service judges every save again.
 */

/** A celebration pack id, as the service names it (cp- and 16 hex characters). */
export const CELEBRATION_PACK_ID_RE = /^cp-[a-f0-9]{16}$/;

export interface CelebrationFileRef {
  bytes: number;
  sha256: string;
}

/** One registered pack, as GET /v1/admin/desktop/mascot/celebrations lists it. */
export interface CelebrationPackRow {
  id: string;
  name: string;
  slug: string;
  theme: string;
  outfit: string;
  durationMs: number;
  /** The pack's files together. */
  bytes: number;
  /** Its files by name: a format-1 pack's clip.webm, clip.mp4 and poster.png, or a format-2 pack's (issue #622) up to six. */
  files: Record<string, CelebrationFileRef>;
  /** 1: one 1536 square rendition; 2: native and 1536 renditions (an older service sends none: 1). */
  format?: 1 | 2;
  /** Whether the pick may choose it and the desktops list it. */
  enabled: boolean;
  registeredBy: string;
  registeredAtMs: number;
}

export interface CelebrationsView {
  /** The revision every save sends back as expectedRevision; a stale one is refused with 409 celebrations_changed. */
  revision: number;
  packs: CelebrationPackRow[];
  limits: { maxPacks: number; maxFileBytes: number; maxManifestBytes: number; durationMs: { min: number; max: number }; maxPackBytes?: number; chunkBytes?: number };
}

/** PUT /v1/admin/desktop/mascot/celebrations. */
export interface SaveCelebrationsBody {
  packs: Array<{ id: string; enabled: boolean }>;
  expectedRevision: number;
}

export const CELEBRATIONS_HINT = 'The clips Gyro plays, large and centred, when a person is recognized with a shout. Each recognition plays one clip, chosen at random among the packs switched on, once per person. The import tool registers the packs; switch them on or off here.';
export const CELEBRATIONS_SAVED_TEXT = 'Saved. Desktops pick up the change at their next check, within a day.';
export const CELEBRATIONS_CHANGED_TEXT = 'Another admin changed the celebration packs after you loaded them, so nothing was saved. The list now shows their change; make yours again.';
export const CELEBRATIONS_UNREADABLE_TEXT = 'The celebration packs could not be read.';
export const CELEBRATIONS_EMPTY_TEXT = 'No celebration pack is registered yet. Import the packs with scripts/import-mascot-actions-apply.ps1 -Celebrations, then switch them on here.';
export const CELEBRATIONS_NONE_ON_TEXT = 'No pack is switched on: a recognition shows in the notifications panel without a celebration until one is.';

/** The switches as the section edits them: pack id to on/off. */
export type CelebrationSwitches = Record<string, boolean>;

export function switchesOf(view: CelebrationsView): CelebrationSwitches {
  return Object.fromEntries(view.packs.map((pack) => [pack.id, pack.enabled]));
}

/** Whether any switch differs from the view the section loaded (so Save is offered). */
export function celebrationsChanged(switches: CelebrationSwitches, view: CelebrationsView): boolean {
  return view.packs.some((pack) => (switches[pack.id] ?? pack.enabled) !== pack.enabled);
}

/** The body Save sends: every pack's switch (the service applies the ones it knows), with the revision loaded. */
export function celebrationsSaveBody(switches: CelebrationSwitches, view: CelebrationsView): SaveCelebrationsBody {
  return { packs: view.packs.map((pack) => ({ id: pack.id, enabled: switches[pack.id] ?? pack.enabled })), expectedRevision: view.revision };
}

/** How many packs are on under the switches as edited. */
export function enabledCount(switches: CelebrationSwitches, view: CelebrationsView): number {
  return view.packs.filter((pack) => switches[pack.id] ?? pack.enabled).length;
}

/** "7.0 s" */
export function durationText(durationMs: number): string {
  return `${(durationMs / 1000).toFixed(1)} s`;
}

/** "38.2 MB" (the pack's files together; decimal megabytes, as Windows shows a download). */
export function bytesText(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** "4 of 10 packs on" */
export function enabledSummaryText(on: number, total: number): string {
  return `${on} of ${total} ${total === 1 ? 'pack' : 'packs'} on`;
}
